import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Flex, Loader, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { CalendarAccountsPreview } from 'src/meetings/components/CalendarAccountsPreview';
import { MeetingsEmptyView } from 'src/meetings/components/MeetingsEmptyView';
import { useCalendars } from 'src/meetings/hooks/useCalendars';
import { useScopedMeetings } from 'src/meetings/hooks/useScopedMeetings';
import {
  assignSpeakerPath,
  meetingDetailPath,
  meetingsBasePath,
  type MeetingScope,
} from 'src/meetings/scope';
import { isPastEvent } from 'src/meetings/utils/date-utils';
import { AddExpectedAttendeesModal } from './AddExpectedAttendeesModal';
import { FamilyMeetingsList } from './FamilyMeetingsList';

type Props = {
  scope: MeetingScope;
  section?: string;
};

const SECTIONS = {
  Upcoming: 'upcoming',
  Past: 'past',
} as const;

const CLIENT_FILTER = {
  ClientsOnly: 'clients-only',
  All: 'all',
} as const;

export function FamilyMeetingsPanel({ scope, section }: Props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isHub = scope.kind === 'all';
  const isPastSection = section === SECTIONS.Past;

  const [clientFilter, setClientFilter] = useState<
    (typeof CLIENT_FILTER)[keyof typeof CLIENT_FILTER]
  >(CLIENT_FILTER.ClientsOnly);
  const { calendars, isLoading: calendarsLoading, justConnected } = useCalendars();

  const { events, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } =
    useScopedMeetings(
      scope,
      isHub
        ? {
            section: isPastSection ? 'past' : 'upcoming',
            clientMeetingsOnly: clientFilter === CLIENT_FILTER.ClientsOnly ? true : undefined,
          }
        : {},
    );

  const [participantsMeetingId, setParticipantsMeetingId] = useState<string | null>(null);

  // Steering (next-best-action) waits for a calendar to sync its first events;
  // fire the same signal the old meetings page did once events first appear.
  const hadEventsRef = useRef(false);
  useEffect(() => {
    if (isHub && !hadEventsRef.current && events.length > 0) {
      hadEventsRef.current = true;
      dispatchAppEvent('LOADED_CALENDAR_EVENTS');
    }
  }, [isHub, events.length]);

  useEffect(() => {
    if (isHub && justConnected) {
      refetch();
    }
  }, [isHub, justConnected]);

  const basePath = meetingsBasePath(scope);

  // Steering deep-links to a specific meeting via ?eventId= (carrying any other
  // params, e.g. generate_agenda); send those on to the meeting detail.
  const eventIdParam = isHub ? searchParams.get('eventId') : null;
  if (eventIdParam) {
    const rest = new URLSearchParams(searchParams);
    rest.delete('eventId');
    const query = rest.toString();
    return (
      <Navigate
        to={`${meetingDetailPath(scope, eventIdParam)}${query ? `?${query}` : ''}`}
        replace
      />
    );
  }

  if (section !== SECTIONS.Upcoming && section !== SECTIONS.Past) {
    const query = searchParams.toString();
    return <Navigate to={`${basePath}/${SECTIONS.Upcoming}${query ? `?${query}` : ''}`} replace />;
  }

  if (isHub && !calendarsLoading && calendars.length === 0) {
    return <MeetingsEmptyView calendars={calendars} isLoading={calendarsLoading} />;
  }

  const links = [
    { label: 'Upcoming', href: `${basePath}/${SECTIONS.Upcoming}` },
    { label: 'Past meetings', href: `${basePath}/${SECTIONS.Past}` },
  ];

  // For the practice-wide hub the query is already filtered to the current
  // section server-side; for a client/family the single fetch holds both.
  const sectionEvents = isHub
    ? events
    : events.filter(event => isPastEvent(event) === isPastSection);

  const participantsMeeting = events.find(event => event.id === participantsMeetingId) ?? null;

  function renderList() {
    if (isLoading) {
      return (
        <Flex py="xxl" justify="center">
          <Loader />
        </Flex>
      );
    }

    if (error) {
      return (
        <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={18} />}>
          Some meetings could not be loaded.
        </Alert>
      );
    }

    return (
      <>
        <FamilyMeetingsList
          meetings={sectionEvents}
          order={isPastSection ? 'desc' : 'asc'}
          showClientLabel={isHub}
          emptyMessage={
            isPastSection ? 'No past meetings to show yet.' : 'No upcoming meetings to show yet.'
          }
          onOpenParticipants={setParticipantsMeetingId}
          onOpenDetails={eventId => navigate(meetingDetailPath(scope, eventId))}
          onGenerateMeetingNote={eventId =>
            navigate(meetingDetailPath(scope, eventId, 'meeting-note'))
          }
          onAssignSpeaker={(eventId, speakerLabel) =>
            navigate(assignSpeakerPath(scope, eventId, speakerLabel))
          }
        />

        {isHub && hasNextPage && (
          <Flex justify="center" mt="lg">
            <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
              {isFetchingNextPage ? 'Loading more...' : 'Load more'}
            </Button>
          </Flex>
        )}
      </>
    );
  }

  function renderBody() {
    return (
      <>
        {isHub && (
          <Flex align="center" justify="space-between" gap="sm" mb="md">
            <UnderlineTabs
              mode="local"
              variant="bordered"
              value={clientFilter}
              onChange={value =>
                setClientFilter(value as (typeof CLIENT_FILTER)[keyof typeof CLIENT_FILTER])
              }
              tabs={[
                { value: CLIENT_FILTER.ClientsOnly, label: 'Clients only' },
                { value: CLIENT_FILTER.All, label: 'All meetings' },
              ]}
            />
            <CalendarAccountsPreview />
          </Flex>
        )}

        {renderList()}
      </>
    );
  }

  return (
    <>
      <PageWithSidebar links={links} activeUrl={`${basePath}/${section}`} variant="profileV2">
        {isHub && (
          <Text size="lg" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="md">
            {isPastSection ? 'Past meetings' : 'Upcoming events'}
          </Text>
        )}
        {renderBody()}
      </PageWithSidebar>
      {participantsMeeting && (
        <AddExpectedAttendeesModal
          opened
          onClose={() => setParticipantsMeetingId(null)}
          scope={scope}
          meeting={participantsMeeting}
        />
      )}
    </>
  );
}
